import React from "react";
import { createBet, updateBet } from "../../services/betService";
import { Bet } from "../../types/Bet";
import { Match } from "../../types/Match";
import { ModalValue } from "./BetModalBody";

interface SubmitBetParams {
  modalValue: ModalValue;
  bet?: Bet;
  match?: Match;
  userId: string;
  bets: Bet[];
  setBets: React.Dispatch<React.SetStateAction<Bet[]>>;
}

/**
 * Persists a score prediction, mirroring the original BetModalFooter logic so
 * both the desktop footer and the mobile bet sheet share a single source of
 * truth. Throws on failure so callers can surface an error and keep the modal
 * open.
 *
 * - `bet` present  → update that existing prediction.
 * - `match` present → update the user's existing prediction for the match, or
 *   create a new one when none exists yet.
 */
export async function submitBet({
  modalValue,
  bet,
  match,
  userId,
  bets,
  setBets,
}: SubmitBetParams): Promise<void> {
  const homeTeamScoreBet = Number(modalValue.homeScore);
  const awayTeamScoreBet = Number(modalValue.awayScore);

  if (bet) {
    const newBet: Bet = { ...bet, homeTeamScoreBet, awayTeamScoreBet };
    await updateBet(bet.id, {
      id: newBet.id,
      homeTeamScoreBet,
      awayTeamScoreBet,
      matchId: newBet.matchId,
      bettorId: newBet.bettorId,
    });
    setBets((prev) => [newBet, ...prev.filter((item) => item.id !== bet.id)]);
    return;
  }

  if (match) {
    const betCurr = bets.filter((b) => b.matchId === match.id);
    if (betCurr.length > 0) {
      const newBet: Bet = { ...betCurr[0], homeTeamScoreBet, awayTeamScoreBet };
      await updateBet(newBet.id, {
        id: newBet.id,
        homeTeamScoreBet,
        awayTeamScoreBet,
        matchId: newBet.matchId,
        bettorId: newBet.bettorId,
      });
      setBets((prev) => [
        ...prev.filter((item) => item.id !== betCurr[0].id),
        newBet,
      ]);
    } else {
      const created = await createBet({
        homeTeamScoreBet,
        awayTeamScoreBet,
        matchId: match.id,
        bettorId: userId,
      });
      setBets((prev) => [...prev, created]);
    }
  }
}
